import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';

const httpClient = new HttpClient();

export interface TomVendingMachineLocation {
  id: number;
  name?: string;
}

export interface TomVendingMachine {
  id: number;
  title: string | null;
  sortOrder: number;
  assetId: string;
  location?: TomVendingMachineLocation | null;
}

export async function fetchTomVendingMachines(locationId?: number | ''): Promise<TomVendingMachine[]> {
  const body: Record<string, unknown> = {
    skip: 0,
    take: 5000,
    search: '',
    orderBy: 'sortOrder',
    orderDirection: 'ASC',
  };
  if (locationId !== undefined && locationId !== null && locationId !== '') {
    body.locationId = locationId;
  }
  const response = await httpClient.post('api/tom-vending/vending-machine/query', body);
  return response.data.data || [];
}

export const useTomVendingMachinesList = (locationId?: number | '' | null) => {
  const fetchAll = locationId === undefined || locationId === null || locationId === '';
  const forLocation =
    typeof locationId === 'number' && !Number.isNaN(locationId) && locationId > 0;
  return useQuery({
    queryKey: ['tom-vending-vending-machines', fetchAll ? 'all' : locationId],
    queryFn: () => fetchTomVendingMachines(fetchAll ? undefined : (locationId as number)),
    enabled: fetchAll || forLocation,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export async function fetchTomVendingMachinesAssignable(forLocationId: number): Promise<TomVendingMachine[]> {
  const response = await httpClient.post('api/tom-vending/vending-machine/query-assignable', {
    forLocationId,
    skip: 0,
    take: 500,
    search: '',
    orderBy: 'sortOrder',
    orderDirection: 'ASC',
  });
  return response.data.data || [];
}

export const useTomVendingMachinesAssignable = (forLocationId: number, enabled: boolean) => {
  return useQuery({
    queryKey: ['tom-vending-vending-machines-assignable', forLocationId],
    queryFn: () => fetchTomVendingMachinesAssignable(forLocationId),
    enabled: enabled && forLocationId > 0 && !Number.isNaN(forLocationId),
    staleTime: 15 * 1000,
  });
};

export const useAssignTomVendingMachineToLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { machineId: number; locationId: number }) => {
      const response = await httpClient.post('api/tom-vending/vending-machine/assign-to-location', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines'] });
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines-assignable'] });
    },
  });
};

export const useRemoveTomVendingMachineFromLocation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { machineId: number }) => {
      const response = await httpClient.post('api/tom-vending/vending-machine/remove-from-location', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines'] });
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines-assignable'] });
    },
  });
};

export const useCreateTomVendingMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await httpClient.post('api/tom-vending/vending-machine/create', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines'] });
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines-assignable'] });
    },
  });
};

export const useUpdateTomVendingMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const response = await httpClient.post('api/tom-vending/vending-machine/update', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines'] });
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines-assignable'] });
    },
  });
};

export const useDeleteTomVendingMachine = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: number }) => {
      const response = await httpClient.post('api/tom-vending/vending-machine/delete', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines'] });
      queryClient.invalidateQueries({ queryKey: ['tom-vending-vending-machines-assignable'] });
    },
  });
};
